import React from 'react';

import { cn } from '../../lib/utils';
import { primitiveColors } from '../../styles';

export enum LoadingAnimationSize {
	Large = 'large',
	Medium = 'medium',
	Small = 'small',
}

export interface ILoadingAnimationProps {
	'size'?: LoadingAnimationSize;
	'className'?: string;
	'aria-label'?: string;
	'data-testid'?: string;
}

interface ILoadingAnimationSizeConfig {
	sizePx: number;
	strokeWidthPx: number;
	hasTrack: boolean;
	arcRatio: number;
}

const LOADING_ANIMATION_DURATION_MS = 640;
const ARC_COLOR = primitiveColors.brandBlue['400'];
const TRACK_COLOR = primitiveColors.zinc['200'];

function getLoadingAnimationSizeConfig(
	size: LoadingAnimationSize
): ILoadingAnimationSizeConfig {
	switch (size) {
		case LoadingAnimationSize.Large:
			return {
				sizePx: 88,
				strokeWidthPx: 10,
				hasTrack: false,
				arcRatio: 0.75,
			};
		case LoadingAnimationSize.Medium:
			return {
				sizePx: 40,
				strokeWidthPx: 5,
				hasTrack: true,
				arcRatio: 0.25,
			};
		case LoadingAnimationSize.Small:
			return {
				sizePx: 16,
				strokeWidthPx: 3,
				hasTrack: true,
				arcRatio: 0.25,
			};
		default: {
			const unhandledSize: never = size;
			throw new Error(`Unhandled LoadingAnimation size: ${unhandledSize}`);
		}
	}
}

function LoadingAnimation({
	size = LoadingAnimationSize.Large,
	className,
	'aria-label': ariaLabel = 'Loading',
	'data-testid': dataTestId = 'loading-animation',
}: Readonly<ILoadingAnimationProps>) {
	const { sizePx, strokeWidthPx, hasTrack, arcRatio } =
		getLoadingAnimationSizeConfig(size);
	const centerPx = sizePx / 2;
	const radiusPx = (sizePx - strokeWidthPx) / 2;
	const circumferencePx = 2 * Math.PI * radiusPx;
	const arcLengthPx = circumferencePx * arcRatio;
	const gapLengthPx = circumferencePx - arcLengthPx;

	return (
		<svg
			className={cn('block', className)}
			width={sizePx}
			height={sizePx}
			viewBox={`0 0 ${sizePx} ${sizePx}`}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role="status"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			data-slot="loading-animation"
			data-size={size}
		>
			<style>{`
        @keyframes loading-animation-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
			{hasTrack && (
				<circle
					cx={centerPx}
					cy={centerPx}
					r={radiusPx}
					stroke={TRACK_COLOR}
					strokeWidth={strokeWidthPx}
				/>
			)}
			<g
				style={{
					transformOrigin: `${centerPx}px ${centerPx}px`,
					animation: `loading-animation-rotate ${LOADING_ANIMATION_DURATION_MS}ms linear infinite`,
				}}
			>
				<circle
					cx={centerPx}
					cy={centerPx}
					r={radiusPx}
					stroke={ARC_COLOR}
					strokeWidth={strokeWidthPx}
					strokeLinecap="butt"
					strokeDasharray={`${arcLengthPx} ${gapLengthPx}`}
				/>
			</g>
		</svg>
	);
}

interface ILoaderFullSizeWrapperProps {
	children: React.ReactNode;
	classNames?: string;
}

function LoaderFullSizeWrapper({
	children,
	classNames,
}: ILoaderFullSizeWrapperProps) {
	return (
		<div
			className={cn(
				'flex w-full flex-col items-center justify-center',
				classNames
			)}
		>
			{children}
		</div>
	);
}

export { LoadingAnimation, LoaderFullSizeWrapper };
